import { ClickAwayListener, PopperPlacementType } from "@mui/material";
import { useSearch } from "@redotech/react-util/search";
import { unique } from "@redotech/util/array";
import Fuse from "fuse.js";
import { memo, useEffect, useMemo, useState } from "react";
import { RedoBadgeColor } from "./arbiter-components/badge/redo-badge";
import { RedoList, RedoListItem } from "./arbiter-components/list/redo-list";
import { Divider } from "./divider";
import { Dropdown } from "./dropdown";
import { Flex } from "./flex";
import PlusIcon from "./icon-old/plus.svg";
import XIcon from "./icon-old/x.svg";

export const SearchableTagDropdown = memo(function SearchableTagDropdown({
  dropdownAnchor,
  availableTags,
  dropdownOpen,
  onCloseDropdown,
  onTagClick,
  allowAdd,
  getTagColor,
  onRemoveTag,
  tagInputPlaceholder,
  searchText,
  setSearchText,
  selectedTags,
  newTagText,
  onPaste,
  placement,
}: {
  availableTags: string[];
  dropdownAnchor: HTMLElement | null;
  dropdownOpen: boolean;
  onCloseDropdown: () => void;
  onTagClick: (tag: string, tagWasSelected: boolean) => void;
  allowAdd: boolean;
  getTagColor?: (tag: string) => RedoBadgeColor;
  onRemoveTag?: (tag: string) => void;
  tagInputPlaceholder: string;
  searchText: string;
  setSearchText: (text: string) => void;
  selectedTags: string[];
  newTagText: string;
  onPaste?: (pastedText: string) => void;
  placement?: PopperPlacementType;
}) {
  const fuseSearcher = useMemo(() => {
    return new Fuse(availableTags, { threshold: 0.3 });
  }, [availableTags]);

  const filteredAvailableTags = useSearch(
    fuseSearcher,
    availableTags,
    searchText,
  );

  const hasExactMatch = availableTags.includes(searchText);

  const [inputRef, setInputRef] = useState<HTMLInputElement | null>(null);

  useEffect(() => {
    if (dropdownOpen) {
      inputRef?.focus();
    }
  }, [inputRef, dropdownOpen]);

  const tagInput = (
    <input
      autoFocus
      onChange={(event) => setSearchText(event.target.value)}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          if (searchText.length === 0) {
            return;
          }
          if (hasExactMatch) {
            onTagClick(
              // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
              filteredAvailableTags[0],
              tagIsSelected(filteredAvailableTags[0]),
            );
          } else {
            onTagClick(searchText, tagIsSelected(searchText));
          }
        }
      }}
      onPaste={(event) => {
        if (onPaste) {
          event.preventDefault();
          const pastedData = event.clipboardData.getData("text");
          onPaste(pastedData);
          setSearchText("");
        }
      }}
      placeholder={tagInputPlaceholder}
      ref={setInputRef}
      style={{ fontSize: "12px" }}
      value={searchText}
    />
  );

  const [focusedIndex, setFocusedIndex] = useState<number | undefined>();

  function tagIsSelected(tag: string | undefined) {
    return selectedTags.includes(tag!);
  }

  const tagItems = useMemo(() => {
    const baseItems = unique(filteredAvailableTags).map<
      RedoListItem<string | undefined>
    >((tag) => ({
      value: tag,
      id: tag,
      type: "badge",
      badge: {
        badgeColor: getTagColor?.(tag) ?? "gray",
        text: tag,
        iconTrailing: onRemoveTag
          ? { type: "icon", onClick: () => onRemoveTag(tag), Icon: XIcon }
          : undefined,
      },
    }));

    if (allowAdd) {
      const createTagItem: RedoListItem<string | undefined> = {
        id: "create-tag-item",
        type: "text",
        leadingItem: { type: "icon", Icon: PlusIcon },
        text: newTagText,
        value: undefined,
      };

      baseItems.push(createTagItem);
    }

    return baseItems;
  }, [allowAdd, newTagText, filteredAvailableTags, getTagColor, onRemoveTag]);

  const disabledItem =
    filteredAvailableTags.length === 1 || hasExactMatch
      ? "create-tag-item"
      : undefined;

  const dropdownItems = (
    <Dropdown
      anchor={dropdownAnchor}
      fitToAnchor={false}
      open={dropdownOpen}
      placement={placement}
    >
      <Flex dir="column" gap="sm">
        {tagInput}
        <Divider />
        <RedoList
          disabledItems={disabledItem}
          focusedIndex={focusedIndex}
          items={tagItems}
          itemSelected={({ item }) => {
            if (!item.value) {
              if (searchText) {
                onTagClick(searchText, tagIsSelected(searchText));
              }
            } else {
              onTagClick(item.value, tagIsSelected(item.value));
            }
          }}
          refToListenTo={inputRef}
          selectedItems={selectedTags}
          setFocusedIndex={setFocusedIndex}
          size="sm"
        />
      </Flex>
    </Dropdown>
  );

  return dropdownOpen ? (
    <ClickAwayListener onClickAway={onCloseDropdown}>
      {dropdownItems}
    </ClickAwayListener>
  ) : undefined;
});
